// src/context/brandVoice/copy.js — every sentence the Brand Voice screen shows for a
// refusal, in ONE pure place (each helper takes `t`).
//
// ⛔⛔ An error is worded from `err.code` / `err.status` and NOTHING else: never
// `err.message` (the server's English), never a server `warning`, never a
// `details.errors[].message`. A 5xx that carried a `request_id` shows it, because that
// reference is the only thing support can act on.
// The three read helpers were moved here from BrandVoicePage.jsx in v184, unchanged, so
// the page, the field rows and the save bar all word a refusal the same way.

export function docErrorCopy(t, err) {
  if (err?.code === 'RESOURCE_NOT_FOUND' || err?.status === 404) return t('brandVoice.errors.noGuidelines');
  if (err?.code === 'BRAND_GUIDELINES_NOT_TENANT_OWNED') return t('brandVoice.errors.notTenantOwned');
  if (err?.status === 403) return t('brandVoice.errors.adminOnly');
  if (err?.status >= 500 && err?.requestId) return t('brandVoice.errors.server', { requestId: err.requestId });
  return t('brandVoice.errors.generic', { code: err?.code || err?.status || '—' });
}

export function voiceErrorCopy(t, err) {
  if (err?.code === 'CONTEXT_ENTITY_NOT_FOUND' || err?.status === 404) return t('brandVoice.errors.noVoice');
  if (err?.status >= 500 && err?.requestId) return t('brandVoice.errors.server', { requestId: err.requestId });
  return t('brandVoice.errors.generic', { code: err?.code || err?.status || '—' });
}

// ⛔ No retry button where a retry cannot change the answer: a workspace without a
// brand-guidelines file (404) and the `default` tenant (422) answer the same way for ever.
export function docErrorRetryable(err) {
  if (err?.code === 'RESOURCE_NOT_FOUND' || err?.status === 404) return false;
  if (err?.code === 'BRAND_GUIDELINES_NOT_TENANT_OWNED' || err?.status === 422) return false;
  return true;
}

/** A failed SAVE part → OUR sentence. `part` is 'doc' | 'voice' | 'voiceEn' (v190: the English voice). */
export function saveErrorCopy(t, err, part) {
  const c = err?.code; const s = err?.status;
  // v190: the English voice before the workspace owns its Italian file (the browser check
  // normally stops this first; this is the backstop). ⛔ Kept ABOVE the 412 branch: it is a
  // 409 about the missing Italian file and must never be worded as a stale etag.
  if (c === 'CONTEXT_BASE_REQUIRED') return t('brandVoice.save.errors.baseRequired');
  if (c === 'CONTEXT_ETAG_MISMATCH' || s === 412) return t(`brandVoice.save.errors.${part}Stale`);
  if (c === 'RUN_ACTIVE') return t('brandVoice.save.errors.runActive');
  if (c === 'BRAND_GUIDELINES_FIELD_INVALID' || c === 'CONTEXT_ENTITY_INVALID') return t(`brandVoice.save.errors.${part}Invalid`);
  if (c === 'SERVICE_UNAVAILABLE' || s === 503) return t('brandVoice.save.errors.unavailable');
  if (s === 403) return t('brandVoice.errors.adminOnly');
  if (s >= 500 && err?.requestId) return t('brandVoice.errors.server', { requestId: err.requestId });
  return t('brandVoice.save.errors.generic', { code: c || s || '—' });
}

/** A failed Freeze / Unlock / Traduci ora → OUR sentence (used as the ConfirmDialog rejection,
 *  which that dialog renders verbatim — so this must already be the user-facing text). */
export function actionErrorCopy(t, err) {
  const c = err?.code; const s = err?.status;
  if (c === 'RUN_ACTIVE') return t('brandVoice.save.errors.runActive');
  if (c === 'SERVICE_UNAVAILABLE' || s === 503) return t('brandVoice.save.errors.unavailable');
  if (c === 'CONTEXT_ETAG_MISMATCH' || s === 412) return t('brandVoice.save.errors.docStale');
  if (s === 403) return t('brandVoice.errors.adminOnly');
  if (s >= 500 && err?.requestId) return t('brandVoice.errors.server', { requestId: err.requestId });
  return t('brandVoice.save.errors.generic', { code: c || s || '—' });
}
